/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-console */
import {
    createContext,
    FC,
    ReactNode,
    useCallback,
    useContext,
    useMemo,
    useState,
} from 'react';
import {Backend, DjangoMessage} from 'types/api';
import {PermissionKeyApp, PermissionResponse} from 'types/permissions';
import {toCamelCase} from 'utils/object';
import useBackendRefresh from './services';

const BackendContext = createContext<any>({});

const useBackend = <T extends Backend<unknown>>() =>
    useContext<
        T & {
            checkFlag: (flag: keyof T['flags']) => boolean;
            isRefreshing: boolean;
            refreshData: (url: string) => Promise<void>;
            setData: (data: T['data']) => void;
        }
    >(BackendContext);

export type BackendProviderProps = {
    appPermissions?: {[K in PermissionKeyApp]?: PermissionResponse};
    children: ReactNode;
    data: any;
    flags?: Record<string, boolean>;
    messages?: DjangoMessage[];
};

const BackendProvider: FC<BackendProviderProps> = ({
    appPermissions,
    children,
    data,
    flags,
    messages,
}) => {
    const {mutateAsync} = useBackendRefresh<BackendProviderProps>();

    const [isRefreshing, setIsRefreshing] = useState(false);
    const [backendData, setBackendData] = useState(data);

    const refreshData = useCallback(
        (url: string) => {
            setIsRefreshing(true);

            return mutateAsync(url)
                .then((response) => {
                    console.log('REFRESH DATA');
                    console.log(response);
                    if (response) setBackendData(response.data.data);
                })
                .catch((error: Error) => {
                    console.error(error);
                })
                .finally(() => {
                    setIsRefreshing(false);
                });
        },
        [mutateAsync],
    );

    const setData = useCallback((newData: any) => {
        setBackendData(newData);
    }, []);

    const checkFlag = useCallback(
        (flag: string) => flags?.[flag] === true,
        [flags],
    );
    const value = useMemo(() => {
        const processedData = toCamelCase({
            appPermissions,
            data: backendData,
        }) || {appPermissions: {}, data: {}};

        return {
            ...processedData,
            checkFlag,
            flags,
            isRefreshing,
            messages,
            refreshData,
            setData,
        };
    }, [
        appPermissions,
        backendData,
        checkFlag,
        flags,
        isRefreshing,
        messages,
        refreshData,
        setData,
    ]);

    return (
        <BackendContext.Provider value={value}>
            {children}
        </BackendContext.Provider>
    );
};

export {BackendProvider, useBackend};
